import { useState, useEffect } from "react";
import { Select } from "antd";
import { Link } from "react-router-dom";
import { register, getRegisterOptions, type RegisterOptions } from "../../services/auth";
import RIcon from "../../components/RIcon";

type Step = "form" | "success";

function RegisterField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">{label}</label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export default function RegisterPage() {
  const [step, setStep] = useState<Step>("form");
  const [form, setForm] = useState({
    username: "", realName: "", employeeId: "", password: "", confirmPassword: "",
    email: "", departmentId: "", jobTypeId: "", workStageId: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deptList, setDeptList] = useState<RegisterOptions["departments"]>([]);
  const [jobList, setJobList] = useState<RegisterOptions["job_types"]>([]);
  const [stageList, setStageList] = useState<RegisterOptions["work_stages"]>([]);

  useEffect(() => {
    getRegisterOptions().then((data) => {
      setDeptList(data.departments || []);
      setJobList(data.job_types || []);
      setStageList(data.work_stages || []);
    }).catch(() => {});
  }, []);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.username.trim()) errs.username = "请输入用户名";
    else if (form.username.length < 2) errs.username = "用户名至少2个字符";
    if (!form.realName.trim()) errs.realName = "请输入真实姓名";
    if (!form.employeeId.trim()) errs.employeeId = "请输入工号";
    if (!form.email.trim()) errs.email = "请输入邮箱";
    if (!form.password.trim()) errs.password = "请输入密码";
    else if (form.password.length < 8) errs.password = "密码至少8位";
    if (form.password !== form.confirmPassword) errs.confirmPassword = "两次密码不一致";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      await register({
        username: form.username,
        password: form.password,
        email: form.email,
        full_name: form.realName,
        employee_id: form.employeeId.trim(),
        department_id: form.departmentId ? Number(form.departmentId) : undefined,
        job_type_id: form.jobTypeId ? Number(form.jobTypeId) : undefined,
        work_stage_id: form.workStageId ? Number(form.workStageId) : undefined,
      } as any);
      setStep("success");
    } catch (err) {
      const message = (err as Error).message || "注册失败";
      if (message.includes("工号")) setErrors({ employeeId: message });
      else if (message.includes("邮箱")) setErrors({ email: message });
      else if (message.includes("用户名")) setErrors({ username: message });
      else setErrors({ email: message });
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (name: string) =>
    `w-full border rounded-lg px-3 h-11 text-base md:text-sm outline-none transition-colors placeholder:text-gray-400 ${
      errors[name] ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-[#0EA5A0]"
    }`;

  if (step === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F8FB]">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-sm">
          <div className="w-16 h-16 bg-[#0EA5A0]/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <RIcon name="checkbox-circle-line" className="text-3xl text-[#0EA5A0]" />
          </div>
          <h2 className="text-xl font-bold text-[#1A2B3C] mb-2">注册申请已提交</h2>
          <p className="text-[#718096] text-sm leading-relaxed mb-8">
            账号已创建，正在等待管理员审核启用。审核通过后即可使用本账号登录系统。
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center w-full h-11 bg-[#0EA5A0] text-white rounded-lg text-sm font-semibold hover:bg-[#0D9590] transition-all whitespace-nowrap"
          >
            返回登录
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20pharmaceutical%20medical%20laboratory%20technology%20background%20with%20teal%20green%20blue%20molecular%20network%20patterns%20modern%20professional%20clean%20design%20scientific%20innovation&width=800&height=1080&seq=register-bg-001&orientation=portrait"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A4A47]/85 to-[#0EA5A0]/60" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png" alt="协和医疗" className="h-9 w-auto brightness-0 invert" />
            <span className="text-white font-semibold text-sm">协和医疗全域AI系统</span>
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-white mb-4">加入我们的<br />知识协作网络</h1>
            <p className="text-white/70 text-sm leading-relaxed">
              注册后，您可以访问企业知识库、使用AI问答、生成专业文档，提升工作效率。
            </p>
            <div className="mt-8 space-y-3">
              {["账号创建后可直接登录", "可绑定部门和工种权限", "安全的内网环境保障数据安全"].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#AAFF45] flex items-center justify-center flex-shrink-0">
                    <RIcon name="check-line" className="text-[#1A2B3C] text-xs" />
                  </div>
                  <span className="text-white/80 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-white/30 text-xs">© 2026 协和医疗 · 内部系统</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-white overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png" alt="协和医疗" className="h-7 w-auto" />
            <span className="text-[#1A2B3C] font-semibold text-sm">协和医疗全域AI系统</span>
          </div>

          <h2 className="text-2xl font-bold text-[#1A2B3C] mb-1">申请注册</h2>
          <p className="text-[#718096] text-sm mb-8">填写信息创建账号，提交后等待管理员审核启用</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <RegisterField label="用户名" error={errors.username}>
                <input type="text" placeholder="登录账号" className={inputClass("username")} value={form.username} onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))} />
              </RegisterField>
              <RegisterField label="真实姓名" error={errors.realName}>
                <input type="text" placeholder="姓名" className={inputClass("realName")} value={form.realName} onChange={(e) => setForm((prev) => ({ ...prev, realName: e.target.value }))} />
              </RegisterField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <RegisterField label="工号" error={errors.employeeId}>
                <input type="text" placeholder="如 R0001" className={inputClass("employeeId")} value={form.employeeId} onChange={(e) => setForm((prev) => ({ ...prev, employeeId: e.target.value }))} />
              </RegisterField>
              <RegisterField label="邮箱" error={errors.email}>
                <input type="email" placeholder="请输入邮箱" className={inputClass("email")} value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
              </RegisterField>
            </div>

            <RegisterField label="密码" error={errors.password}>
              <div className={`flex items-center border rounded-lg px-3 h-11 gap-2 transition-colors ${
                errors.password ? "border-red-400 bg-red-50" : "border-gray-200 focus-within:border-[#0EA5A0]"
              }`}>
                <input type="password" placeholder="至少8位密码" className="flex-1 bg-transparent outline-none text-base md:text-sm placeholder:text-gray-400" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} />
              </div>
            </RegisterField>

            <RegisterField label="确认密码" error={errors.confirmPassword}>
              <input type="password" placeholder="再次输入密码" className={inputClass("confirmPassword")} value={form.confirmPassword} onChange={(e) => setForm((prev) => ({ ...prev, confirmPassword: e.target.value }))} />
            </RegisterField>

            <RegisterField label="所属部门">
              <Select
                showSearch
                allowClear
                placeholder="请选择部门"
                style={{ width: "100%" }}
                value={form.departmentId || undefined}
                onChange={(v) => setForm((prev) => ({ ...prev, departmentId: (v ?? "") as string, jobTypeId: "" }))}
                optionFilterProp="label"
                options={deptList.map((d) => ({ value: String(d.id), label: d.name }))}
              />
            </RegisterField>

            <RegisterField label="工种">
              <Select
                showSearch
                allowClear
                placeholder="请选择工种"
                style={{ width: "100%" }}
                value={form.jobTypeId || undefined}
                onChange={(v) => setForm((prev) => ({ ...prev, jobTypeId: (v ?? "") as string }))}
                optionFilterProp="label"
                options={jobList.map((j) => ({ value: String(j.id), label: j.name }))}
              />
            </RegisterField>

            <button type="submit" disabled={loading} className="w-full h-11 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer mt-2 whitespace-nowrap">
              {loading ? <><RIcon name="loader-4-line" className="animate-spin" />提交中...</> : "立即注册"}
            </button>
          </form>

          <p className="text-center text-sm text-[#718096] mt-5">
            已有账号？
            <Link to="/login" className="text-[#0EA5A0] font-medium hover:underline ml-1">返回登录</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
